import { Nullable } from '@ui/types/utility';
import BigNumber from 'bignumber.js';
import { isNil } from 'lodash-es';

import { getClosePositionFee } from './getClosePositionFee';

export type GetLiqPriceParams = {
  entryPrice: BigNumber;
  liqLostP: number | undefined;
  countableCollateralUsd: BigNumber;
  positionQty: BigNumber;
  fundingFee: Nullable<BigNumber>;
  holdingFee: Nullable<BigNumber>;
  closeFee: Nullable<ReturnType<typeof getClosePositionFee>>;
  isLong: boolean;
  slippageP?: number;
};

/**
 * For long:
 * (entryPrice - liqPrice) * positionQty + fees = collateralUsd * liqLostP
 *
 * For short:
 * (liqPrice - entryPrice) * positionQty + fees = collateralUsd * liqLostP
 *
 * liqPriceDelta = (entryPrice - liqPrice)
 *
 * liqPriceDelta = (collateralUsd * liqLostP - fees) / positionQty
 *
 * fees = funding, holding, close fee
 *
 */
const getLiqPriceDelta = ({
  liqLostP,
  countableCollateralUsd,
  positionQty,
  fundingFee,
  holdingFee,
  closeFee,
}: Omit<GetLiqPriceParams, 'entryPrice' | 'isLong' | 'slippageP'>) => {
  if (isNil(liqLostP) || !fundingFee || !holdingFee || !closeFee)
    return undefined;
  const countableFeesUsd = fundingFee
    .plus(holdingFee)
    .plus(closeFee.finalCloseFee);
  const collateralUsdInLiqLostP =
    countableCollateralUsd.multipliedBy(liqLostP);

  const collateralUsdInLiqLostPAfterFee =
    collateralUsdInLiqLostP.minus(countableFeesUsd);

  if (positionQty.isZero()) return undefined;

  return collateralUsdInLiqLostPAfterFee.div(positionQty);
};

/**
 *
 * isLong = true :
 *
 * liqPrice = (entry Price - liqPriceDelta) / (1 - slippageP)
 *
 * isLong = false :
 *
 * liqPrice = (entry Price + liqPriceDelta) / (1 + slippageP)
 *
 * @returns
 */
export const getLiqPrice = ({
  entryPrice,
  positionQty,
  isLong,
  liqLostP,
  countableCollateralUsd, // collateralInUsd
  fundingFee,
  holdingFee,
  closeFee,
  slippageP,
}: GetLiqPriceParams) => {
  if (isNil(liqLostP)) return undefined;

  const liqPriceDelta = getLiqPriceDelta({
    liqLostP,
    countableCollateralUsd,
    positionQty,
    closeFee,
    fundingFee,
    holdingFee,
  });

  if (isNil(liqPriceDelta)) return undefined;

  if (isLong) {
    let liqPrice = entryPrice.minus(liqPriceDelta);

    if (slippageP) {
      liqPrice = liqPrice.div(1 - slippageP);
    }

    // as it would not make sense to be negative
    return BigNumber.max(liqPrice, 0);
  } else {
    let liqPrice = entryPrice.plus(liqPriceDelta);

    if (slippageP) {
      liqPrice = liqPrice.div(1 + slippageP);
    }

    return liqPrice;
  }
};
